/**
 * Syncs product-dimensions section image with variant metafield
 * custom.dimensions_image when the selected color/variant changes.
 */
(function () {
  const jsonCache = new Map();
  const preloaded = new Set();
  const MAX_CONCURRENT = 3;

  function parseJsonScript(id) {
    if (jsonCache.has(id)) return jsonCache.get(id);
    const el = document.getElementById(id);
    if (!el) {
      jsonCache.set(id, {});
      return {};
    }
    try {
      const data = JSON.parse(el.textContent.trim() || '{}');
      jsonCache.set(id, data);
      return data;
    } catch (error) {
      console.error('Failed to parse', id, error);
      jsonCache.set(id, {});
      return {};
    }
  }

  function getImageForVariant(sectionId, variantId) {
    if (!variantId) return null;
    const mediaMap = parseJsonScript(`DimensionsMediaMap-${sectionId}`);
    const colorMap = parseJsonScript(`DimensionsColorMap-${sectionId}`);
    const variantKey = String(variantId);

    if (mediaMap[variantKey]?.src) return mediaMap[variantKey];

    const color = colorMap[variantKey];
    if (!color) return null;
    const colorNorm = String(color).trim().toLowerCase();

    for (const [id, image] of Object.entries(mediaMap)) {
      const mapped = colorMap[id];
      if (!mapped || !image?.src) continue;
      if (String(mapped).trim().toLowerCase() === colorNorm) return image;
    }
    return null;
  }

  function applyImage(img, image) {
    if (!img || !image?.src) return;

    img.removeAttribute('srcset');
    if (image.srcset) img.setAttribute('srcset', image.srcset);
    img.src = image.src;
    if (image.alt != null) img.alt = image.alt;
    if (image.width) img.width = image.width;
    if (image.height) img.height = image.height;
    img.hidden = false;

    const placeholder = img.parentElement?.querySelector('.placeholder-svg');
    if (placeholder) placeholder.remove();
  }

  function syncSection(root, variantId) {
    const sectionId = root.dataset.sectionId;
    if (!sectionId || !variantId) return;

    const image = getImageForVariant(sectionId, variantId);
    if (!image?.src) return;

    const slot = root.querySelector('[data-dimensions-image]');
    if (!slot) return;

    let img = slot.querySelector('img');
    if (!img) {
      img = document.createElement('img');
      img.loading = 'lazy';
      img.decoding = 'async';
      img.sizes = '(min-width: 750px) min(1100px, 100vw), 100vw';
      slot.innerHTML = '';
      slot.appendChild(img);
    }
    applyImage(img, image);
    root.hidden = false;
  }

  function getCurrentVariantId() {
    const productInfo = document.querySelector('product-info[data-section]');
    if (!productInfo) return null;
    const sectionId = productInfo.dataset.section;
    return (
      productInfo.querySelector(`#product-form-${sectionId} input[name="id"]`)?.value ||
      productInfo.querySelector('input[name="id"]')?.value ||
      null
    );
  }

  function syncAll(variantId) {
    const id = variantId || getCurrentVariantId();
    if (!id) return;
    document.querySelectorAll('[data-product-dimensions]').forEach((root) => {
      syncSection(root, id);
    });
  }

  function preloadOne(image) {
    return new Promise((resolve) => {
      const img = new Image();
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      img.onload = done;
      img.onerror = done;
      if (image.srcset) img.srcset = image.srcset;
      img.sizes = '(min-width: 750px) min(1100px, 100vw), 100vw';
      img.src = image.src;
      if (img.complete) done();
    });
  }

  function runQueue(queue) {
    let index = 0;
    let active = 0;
    return new Promise((resolve) => {
      const next = () => {
        if (index >= queue.length && active === 0) {
          resolve();
          return;
        }
        while (active < MAX_CONCURRENT && index < queue.length) {
          const item = queue[index++];
          active += 1;
          preloadOne(item).finally(() => {
            active -= 1;
            next();
          });
        }
      };
      next();
    });
  }

  function schedulePreload(sectionId) {
    if (preloaded.has(sectionId)) return;
    preloaded.add(sectionId);

    const start = () => {
      const idle = (cb) =>
        typeof requestIdleCallback === 'function' ? requestIdleCallback(cb, { timeout: 2000 }) : setTimeout(cb, 200);

      idle(() => {
        const mediaMap = parseJsonScript(`DimensionsMediaMap-${sectionId}`);
        const currentId = getCurrentVariantId();
        const current = getImageForVariant(sectionId, currentId);
        const currentSrc = current?.src || '';
        const seen = new Set();
        const queue = [];

        for (const image of Object.values(mediaMap)) {
          if (!image?.src || seen.has(image.src) || image.src === currentSrc) continue;
          seen.add(image.src);
          queue.push(image);
        }

        if (queue.length) runQueue(queue);
      });
    };

    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  function initRoot(root) {
    const sectionId = root.dataset.sectionId;
    if (!sectionId || !document.getElementById(`DimensionsMediaMap-${sectionId}`)) return;

    syncAll(getCurrentVariantId());
    schedulePreload(sectionId);
  }

  function bindVariantListeners() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variantId = event?.data?.variant?.id;
        if (!variantId) return;
        syncAll(variantId);
      });
    }

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.name !== 'id' || !input.closest('product-info')) return;
      if (!input.value) return;
      syncAll(input.value);
    });
  }

  function init() {
    document.querySelectorAll('[data-product-dimensions]').forEach(initRoot);
    bindVariantListeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
